import env from 'env';
import posthog from 'posthog-js';
import { ProfileInterface } from 'types';

const posthogApiKey = env.POSTHOG_KEY;
const posthogHost = env.POSTHOG_HOST;
const isProduction = process.env.NODE_ENV === 'production';
const isPosthogEnabled = Boolean(posthogApiKey && posthogHost && isProduction);
// Use null until identity is known: true means internal, false means external.
let isInternalUser: boolean | null = null;

// This is redundant, but we need to check if the key is set to avoid errors
if (isPosthogEnabled && posthogApiKey) {
    posthog.init(posthogApiKey, {
        api_host: posthogHost,
        capture_exceptions: true,
        person_profiles: 'identified_only',
    });
}

function buildPosthogPersonProperties(profile: ProfileInterface, isInternal: boolean) {
    const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ').trim();
    const normalizedPhone = `${profile.phone_country_code || ''}${profile.phone || ''}`.trim();

    return {
        is_internal: isInternal,
        profile_id: profile.pk,
        username: profile.username,
        email: profile.email,
        phone: profile.phone || null,
        phone_country_code: profile.phone_country_code || null,
        normalized_phone: normalizedPhone || null,
        first_name: profile.first_name || null,
        last_name: profile.last_name || null,
        full_name: fullName || null,
        account_type: profile.accountType || null,
        super_admin: profile.superuser,
        spaceforce_admin: profile.spaceforce_admin,
        is_enforcer: profile.isEnforcer,
        is_renter: profile.is_renter,
        affiliation_role: profile.affiliation?.role ?? null,
        affiliation_type: profile.affiliation?.type ?? null,
        affiliation_id: profile.affiliation?.pk ?? null,
        organizations_count: profile.organizations?.length ?? 0,
        affiliations_count: profile.affiliations?.length ?? 0,
    };
}

export function identifyPosthogUser(profile: ProfileInterface) {
    if (!isPosthogEnabled) {
        return;
    }

    const email = profile.email?.trim().toLowerCase() || '';
    const isInternal = profile.superuser || email.endsWith('@airgarage.com') || email.endsWith('@airgara.ge');
    isInternalUser = isInternal;
    const distinctId = String(profile.pk);
    posthog.identify(distinctId, buildPosthogPersonProperties(profile, isInternal));
    posthog.reloadFeatureFlags();
}

export function resetPosthogUser() {
    if (!isPosthogEnabled) {
        return;
    }

    isInternalUser = null;
    posthog.reset(true);
}

export function capturePosthogEvent(event: string, properties: { [index: string]: unknown }): void {
    if (!isPosthogEnabled) return;

    posthog.capture(event, {
        ...properties,
        app: 'owner-dashboard',
        environment: 'production',
        is_internal: isInternalUser,
    });
}

export default posthog;
